import { Locales } from '@griply/types';

import {
  buildDateSeparator,
  buildDayListSeparator,
  getDayPattern,
  getMonthPattern,
  getRruleKeywords,
} from './locale-vocabulary';
import { buildFormatContext, RRULE_FORMATTERS } from './rrule-formatters';
import { buildMatchContext, matchRrule } from './rrule-matchers';

// Generates the regex pattern for recurrence rules like "every friday".
export const getRrulePatternContent = (language: Locales) => {
  const k = getRruleKeywords(language);
  const dayPattern = getDayPattern(language);
  const monthPattern = getMonthPattern(language);

  const ordinalPattern = `\\d{1,2}(?:${k.ordinals})?`;
  const dayListSeparator = buildDayListSeparator(k.and);
  const ordinalListPattern = `${ordinalPattern}(?:${dayListSeparator}${ordinalPattern})*`;

  const dateSeparator = buildDateSeparator(k.of);
  // eslint-disable-next-line max-len -- regex
  const monthDayPattern = `(?:(?:${monthPattern})${dateSeparator}${ordinalPattern}|${ordinalPattern}${dateSeparator}${monthPattern})`;

  const ordinalWordPattern = `(?:${k.first}|${k.second}|${k.third}|${k.fourth}|${k.fifth}|${k.last})`;

  // Most specific matches first.
  const patterns = [
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+${monthDayPattern}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalListPattern}`,
    `\\d+\\s+(?:${k.weeks}|${k.week})\\s+${k.on}\\s+(?:${dayPattern}(?:,\\s*${dayPattern})*)`,
    // eslint-disable-next-line max-len -- regex
    `\\d+\\s+(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s+)?${ordinalPattern}(?:,\\s*${ordinalPattern})*`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.weekday}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.weekend}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${k.day}`,
    // eslint-disable-next-line max-len -- regex
    `\\d+\\s+(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${dayPattern}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.months}|${k.month})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+${dayPattern}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+(?:${dayPattern}|${k.weekday}|${k.weekend})\\s+${k.in}\\s+${monthPattern}`,
    // eslint-disable-next-line max-len -- regex
    `(?:\\d+\\s+)?(?:${k.years}|${k.year})\\s+${k.on}\\s+(?:${k.the}\\s*)?${ordinalWordPattern}\\s+(?:${dayPattern}|${k.weekday}|${k.weekend})`,

    // Negative lookaheads keep these from swallowing the "on ..." forms above.
    `\\d+\\s+(?:${k.days}|${k.day})(?!\\s+${k.on}\\b)`,
    `\\d+\\s+(?:${k.weeks}|${k.week})(?!\\s+${k.on}\\b)`,
    `\\d+\\s+(?:${k.months}|${k.month}|${k.years}|${k.year})(?!\\s+${k.on}\\b)`,

    monthDayPattern,
    `${ordinalWordPattern}\\s+${dayPattern}`,
    k.weekday,
    `(?:${dayPattern}(?:,\\s*${dayPattern})*)`,
    k.day,
    k.week,
    k.month,
    k.year,
    `(?:${ordinalListPattern})(?=\\s|\\u00A0|\\u200B|$)`,
  ];

  // No trailing `\b` — the per-pattern lookaheads own the match boundary.
  // eslint-disable-next-line max-len -- regex
  return `(?:\\b${k.every}\\s+(?:${patterns.join('|')})|\\b${k.daily}\\b|\\b${k.weekly}\\b|\\b${k.monthly}\\b|\\b${k.yearly}\\b)`;
};

/**
 * Parses a recurrence string (e.g., "every fri, mon") into an RRULE string.
 */
export const parseRrule = (rruleStr: string, language: Locales) => {
  if (!rruleStr) {
    return null;
  }

  const normalized = rruleStr.toLowerCase().trim().replace(/\s+/g, ' ');
  const rrule = matchRrule(normalized, buildMatchContext(language));

  return rrule ? { rrule, rruleString: rruleStr } : null;
};

/**
 * Converts an RRULE string into a natural language phrase.
 */
export const parseRruleToString = (rrule: string, language: Locales) => {
  if (!rrule) {
    return '';
  }

  const parts = rrule
    .toUpperCase()
    .replace(/^RRULE:/, '')
    .split(';')
    .reduce<Record<string, string>>((acc, part) => {
      const [key, value] = part.split('=');
      if (key && value) {
        acc[key] = value.trim();
      }
      return acc;
    }, {});

  const ctx = buildFormatContext(language);
  const rendered = RRULE_FORMATTERS.reduce<string | null>(
    (found, formatter) => found ?? formatter(parts, ctx),
    null,
  );

  return rendered ?? rrule;
};
